import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid, ptBR } from '@mui/x-data-grid';
import { FilterAlt, Home as HomeIcon, Print } from '@mui/icons-material';
import { ViewDetailsIcon } from '../../../common/Icons';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { useAppContext } from '../../../../contexts/AppContext';
import { checkPermissionsAndRedirect, getFormErrorMessage, verifyFieldsAreFilled } from '../../../../configs/functions';
import { listaEmpresas } from '../../management/companies/configs/functions';
import { exportRetiradasToPDF } from './configs/utils';
import { getRetiradaDetails, getRetiradasData } from './configs/functions';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { TooltipSelectDisabled } from '../../../common/TooltipSelectDisabled';
import { listaAreas, listaSetores } from '../../management/organization/configs/functions';
import {carregaEquipamentos, carregaEquipamentosAll} from '../equipments/configs/functions';
import { CustomToolbarReports } from '../../../common/CustomToolbarReports';
import { RetiradaDetailsModal } from './RetiradaDetailsModal';
import { grey } from '@mui/material/colors';
import DateRangePicker from '../../../common/DateRangePicker';
import { AuthCaptureCell } from './components/AuthCaptureCell';
import { formatDateTime, parseDateTime } from '../../../../utils/format-date';

export function RelatorioRetiradas() {
  const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();
  const { dadosUsuario, getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

  // estados para controle de paginacao
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(20);
  // const [numTotalItems, setNumTotalItems] = useState(0);

  const [supplyMachine, setDataSupplyMachine] = useState([]);

  const initialDataInicio = dayjs().subtract(1, 'month');
  const initialDataFim = dayjs();

  const [dateRange, setDateRange] = useState([initialDataInicio, initialDataFim]);

  const [empresas, setEmpresas] = useState([]);
  const [setores, setSetores] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);

  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [retiradaDetails, setRetiradaDetails] = useState(null);

  const {
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const columns = useMemo(() => [
    { field: 'id', headerName: 'Identificador', minWidth: 100, valueGetter: (params) => params.row.idRetirada },
    { field: 'codigo', headerName: 'Código', minWidth: 150, valueGetter: (params) => params.row.codigoRetirada },
    { field: 'nomeEmpresa', headerName: 'Empresa', minWidth: 200 },
    {
      field: 'nomeSetor',
      headerName: 'Setor',
      minWidth: 200,
      valueGetter: (params) => {
        if (!params.row.nomeSetor) return 'Não atribuído';
        const codigo = params.row.codigoSetor;
        const nome = params.row.nomeSetor;

        if (codigo && nome) return `${codigo} - ${nome}`;
        if (codigo) return `${codigo}`;
        if (nome) return `${nome}`;
        return '';
      },
      renderCell: (params) => {
        const hasValue = params.value && params.value !== 'Não atribuído';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
        {params.value}
      </span>
        );
      }
    },
    {
      field: 'nomeArea',
      headerName: 'Área',
      minWidth: 200,
      valueGetter: (params) => {
        const codigo = params.row.codigoArea;
        const nome = params.row.nomeArea;
        return codigo && nome ? `${codigo} - ${nome}` : codigo || nome || '';
      },
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
          {params.value || 'Não atribuído'}
        </span>
        );
      }
    },
    {
      field: 'nomeCentroCusto',
      headerName: 'Centro de Custo',
      minWidth: 200,
      valueGetter: (params) => {
        const codigo = params.row.codigoCentroCusto;
        const nome = params.row.nomeCentroCusto;
        return codigo && nome ? `${codigo} - ${nome}` : codigo || nome || '';
      },
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
          {params.value || 'Não atribuído'}
        </span>
        );
      }
    },
    {
      field: 'nomeGrupo',
      headerName: 'Grupo',
      minWidth: 200,
      valueGetter: (params) => {
        const codigo = params.row.codigoGrupo;
        const nome = params.row.nomeGrupo;
        return codigo && nome ? `${codigo} - ${nome}` : codigo || nome || '';
      },
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
          {params.value || 'Não atribuído'}
        </span>
        );
      }
    },
    {
      field: 'nomeFuncao',
      headerName: 'Função',
      minWidth: 200,
      valueGetter: (params) => {
        const codigo = params.row.codigoFuncao;
        const nome = params.row.nomeFuncao;
        return codigo && nome ? `${codigo} - ${nome}` : codigo || nome || '';
      },
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
          {params.value || 'Não atribuído'}
        </span>
        );
      }
    },
    {
      field: 'nomeEquipamento',
      headerName: 'Equipamento',
      minWidth: 200,
      valueGetter: (params) => params.row.nomeEquipamento || 'Retirada manual aprovada',
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
          {params.value}
        </span>
        );
      }
    },
    {
      field: 'tipoEquipamento',
      headerName: 'Tipo de Equipamento',
      minWidth: 150,
      flex: 1,
      valueFormatter: (params) =>
          params.value
              ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
              : '',
    },
    {
      field: 'tipoAutenticacao',
      headerName: 'Autenticação',
      minWidth: 200,
      flex: 1,
      renderCell: (params) =>
        <AuthCaptureCell
          tipoAutenticacao={params.row.tipoAutenticacao}
          nomeFuncionario={params.row.nomeFuncionario}
          faceBiometricPhoto={params.row.faceBiometricPhoto}
        />
    },
    {
      field: 'matriculaFuncionario', headerName: 'Matrícula Funcionário', minWidth: 200
    },
    {
      field: 'nomeFuncionario', headerName: 'Nome Funcionário', minWidth: 200
    },
    {
      field: 'matriculaResponsavel',
      headerName: 'Matrícula Responsável',
      minWidth: 200,
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
        {params.value || 'Não atribuído'}
      </span>
        );
      }
    },
    {
      field: 'responsavelEntrega',
      headerName: 'Responsável Entrega',
      minWidth: 200,
      renderCell: (params) => {
        const hasValue = params.value && params.value !== '';
        return (
            <span style={{
              color: hasValue ? 'inherit' : '#aaa',
              fontStyle: hasValue ? 'normal' : 'italic'
            }}>
        {params.value || 'Não atribuído'}
      </span>
        );
      }
    },
    { field: 'produtoCA', headerName: 'CA', minWidth: 110 },
    { field: 'produtoNome', headerName: 'Produto', minWidth: 250 },
    { field: 'produtoCodExterno', headerName: 'Cód. Produto', minWidth: 110 },
    { field: 'quantidade', headerName: 'Quantidade', minWidth: 80 },
    {
      field: 'dataRetirada',
      headerName: 'Data',
      minWidth: 200,
      type: 'dateTime',
      valueGetter: (params) => parseDateTime(params.row?.dataRetirada),
      renderCell: (params) => formatDateTime(params.row?.dataRetirada, "DD/MM/YYYY HH:mm:ss"),
    },
    {
      field: 'proxRetirada',
      headerName: 'Próxima retirada',
      minWidth: 200,
      valueGetter: (params) => params?.row?.proxRetirada || ''
    },
    { field: 'retirada_extra', headerName: 'Retirada Extra', minWidth: 120, valueGetter: (params) => params.row.retirada_extra ? 'Sim' : 'Não' },
    { field: 'numeroMotorPorta', headerName: 'Número motor/porta', minWidth: 200, valueGetter: (params) => params.row.numeroMotorPorta ? params.row.numeroMotorPorta : '' },
    { field: 'is_devolucao', headerName: 'Tipo', minWidth: 120, valueGetter: (params) => params.row.is_devolucao ? 'Devolução' : 'Retirada' },
    { field: 'consumidoApi', headerName: 'Consumido via Api', minWidth: 150, valueGetter: (params) => params.row.consumidoApi ? 'Sim' : 'Não' },
    {
      field: "action",
      headerName: "Ações",
      sortable: false,
      renderCell: (params) => {
        const onClick = async (e) => {
          e.stopPropagation();
          try {
            const data = await getRetiradaDetails(params.row.idRetirada);
            setRetiradaDetails(data.data.data);
            setModalOpen(true);
          } catch (error) {
            exibirAlerta('Erro', 'Não foi possível obter os detalhes da retirada', 'error');
          }
        };

        return (
          <Button onClick={onClick} size='small' color="primary" variant='contained' sx={{ minWidth: 30 }}>
            <Print fontSize='small' />
          </Button>
        )
      }
    },
  ], [exibirAlerta]);

  const { data: empresasData, error: listaEmpresasError, isLoading: isLoadingEmpresasData, isPending } = useQuery({
    queryKey: ['listaEmpresas'],
    queryFn: async () => {
      const response = await listaEmpresas(null, null, null, true);
      const empresasData = response?.data.data;

      let defaultEmpresaId = null;
      const selectedEmpresaIdSession = getEmpresaIdSession();
      if (selectedEmpresaIdSession) {
        defaultEmpresaId = selectedEmpresaIdSession;
      } else {
        defaultEmpresaId = empresasData[0].id;
        setEmpresaIdSession(empresasData[0].id);
      }

      setSelectedEmpresaId(defaultEmpresaId);

      const defaultValues = {
        id_empresas: defaultEmpresaId,
        id_setor: '',
        id_area: '',
        id_equipamento: '',
        data_inicio: initialDataInicio,
        data_fim: initialDataFim,
      };
      reset(defaultValues);

      return response;
    },
    refetchOnWindowFocus: false,
  });

  const carregaSetores = async (empresaId) => {
    exibirDialog('Carregando setores...');
    try {
      const filterAll = { id_empresas: Array.isArray(empresaId) ? empresaId : [empresaId], isFilter: true };
      const res = await listaSetores(filterAll);
      setSetores(res.data.data);
    } catch (error) {
      console.error('Erro ao carregar Setores:', error);
    } finally {
      handleCloseDialog();
    }
  }

  const carregaAreas = async (empresaId) => {
    exibirDialog('Carregando Áreas...');
    try {
      const filterAll = { id_empresas: Array.isArray(empresaId) ? empresaId : [empresaId], isFilter: true };
      const res = await listaAreas(filterAll);
      setAreas(res.data.data);
    } catch (error) {
      console.error('Erro ao carregar áreas:', error);
    } finally {
      handleCloseDialog();
    }
  }

  const listaEquipamentos = async (empresaId) => {
    try {
      exibirDialog('Carregando Equipamentos...');
      const filterAllMachines = { id_empresas: Array.isArray(empresaId) ? empresaId : [empresaId], isFilter: true };
      const response = await carregaEquipamentosAll(filterAllMachines);

      const equips = (response.data.equipamentos?.data || []).map(e => ({ ...e, tipoDispositivo: 'equipamento' }));
      const arms = (response.data.armarios?.data || []).map(a => ({ ...a, tipoDispositivo: 'armario' }));

      setEquipamentos([...equips, ...arms]);
    } catch (error) {
      exibirAlerta('Erro ao carregar os Equipamentos', '', 'error');
    } finally {
      handleCloseDialog();
    }
  }

  async function loadReportData(id_empresa, id_setor, id_area, id_equipamento, data_inicio, data_fim) {
    if (!id_empresa) return;

    try {
      setIsLoading(true);

      data_inicio = dayjs(data_inicio).format('YYYY-MM-DD');
      data_fim = dayjs(data_fim).format('YYYY-MM-DD');

      // para poder exportar os dados completos, é necessário utilizar paginationMode client
      // pois se será possível, carregar os dados para exportar, então também será possível carregar para exibir

      const filter = {
        data_inicio,
        data_fim,
        // limit: rowsPerPage,
        // offset: page * rowsPerPage,
      };

      if (id_empresa === 'all') {
        filter.empresa = 1
      } else {
        filter.id_empresa = id_empresa
      }
      if (id_setor) filter.id_setor = id_setor;
      if (id_area) filter.id_area = id_area;
      if (id_equipamento) {
        const [tipoDispositivo, dispositivoId] = String(id_equipamento).split(':');
        if (tipoDispositivo === 'armario') {
          filter.id_armario = dispositivoId;
        } else {
          filter.id_equipamento = dispositivoId;
        }
      }

      const res = await getRetiradasData(filter);
      setDataSupplyMachine(res.data.retiradas);
      // setNumTotalItems(res.data.numero_total);
    } catch (error) {
      console.log(error);
      exibirAlerta('Ops', 'Ocorreu um erro ao carregar dados do relatório.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSearched(true);
      loadReportData(data.id_empresas, data.id_setor, data.id_area, data.id_equipamento, data.data_inicio, data.data_fim);
    } catch (error) {
      exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error');
      setIsSearched(false);
    }
  };

  function getDataEmpresa(empresaId) {
    carregaSetores(empresaId);
    carregaAreas(empresaId);
    listaEquipamentos(empresaId);
  }

  useEffect(() => {
    if (empresasData)  setEmpresas(empresasData?.data?.data || []);
  }, [empresasData]);
  const isLoadingEmpresas = isLoadingEmpresasData || isPending;

  useEffect(() => {
    const requiredPermissionsView = ["admin", "admin_relatorio"];
    checkPermissionsAndRedirect(requiredPermissionsView);

    return () => {
      reset();
    }
  }, []);

  useEffect(() => {
    if (selectedEmpresaId) {
      getDataEmpresa(selectedEmpresaId);
    }
  }, [selectedEmpresaId]);

  useEffect(() => {
    const id_empresas = selectedEmpresaId;
    const id_setor = getValues('id_setor');
    const id_area = getValues('id_area');
    const id_equipamento = getValues('id_equipamento');
    const data_inicio = getValues('data_inicio')
    const data_fim = getValues('data_fim')

    if (!id_empresas || !data_inicio || !data_fim) {
      return;
    }

    loadReportData(id_empresas, id_setor, id_area, id_equipamento, data_inicio, data_fim);
  }, []);

  useEffect(() => {
    if (listaEmpresasError) {
      exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
    }
  }, [exibirAlerta, listaEmpresasError])

  function exportDataToPDF(visibleColumns, reportName) {
    if (!getValues('id_empresas')) {
      return exibirAlerta('Ops', 'Selecione ao menos empresa', 'warning');
    }

    const dataInicio = getValues('data_inicio');
    const dataFim = getValues('data_fim');

    if (!dataInicio || !dataFim) {
      return exibirAlerta('Ops', 'Selecione um período para exportar', 'warning');
    }

    const formattedTableData = supplyMachine.map((row) => ({
      ...row,
      nomeSetor: (() => {
        const codigo = row.codigoSetor;
        const nome = row.nomeSetor;
        if (codigo && nome) return `${codigo} - ${nome}`;
        if (codigo) return `${codigo}`;
        if (nome) return `${nome}`;
        return '';
      })(),
      nomeArea: (() => {
        const codigo = row.codigoArea;
        const nome = row.nomeArea;
        if (codigo && nome) return `${codigo} - ${nome}`;
        return codigo || nome || '';
      })(),
      nomeCentroCusto: (() => {
        const codigo = row.codigoCentroCusto;
        const nome = row.nomeCentroCusto;
        if (codigo && nome) return `${codigo} - ${nome}`;
        return codigo || nome || '';
      })(),
      nomeGrupo: (() => {
        const codigo = row.codigoGrupo;
        const nome = row.nomeGrupo;
        if (codigo && nome) return `${codigo} - ${nome}`;
        return codigo || nome || '';
      })(),
      nomeFuncao: (() => {
        const codigo = row.codigoFuncao;
        const nome = row.nomeFuncao;
        if (codigo && nome) return `${codigo} - ${nome}`;
        return codigo || nome || '';
      })()
    }));

    try {
      exportRetiradasToPDF({
        tableData: formattedTableData,
        visibleColumns,
        reportName,
        dataInicio: dayjs(dataInicio).format('DD/MM/YYYY'),
        dataFim: dayjs(dataFim).format('DD/MM/YYYY'),
        selectEmpresa: empresas.find(empresa => empresa.id === selectedEmpresaId),
        dadosUsuario
      })
      exibirAlerta('Relatório gerado com sucesso!', '', 'success');
    } catch (error) {
      exibirAlerta('Erro', 'Ocorreu um erro ao exportar dados para PDF', 'error');
      console.log(error);
    }
  }

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="stretch"
      spacing={3}
    >
      <Grid item xs={12}>
        <Breadcrumbs>
          <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
          <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao>
          <LinkNavegacao to='/epis/relatorios'>Relatórios</LinkNavegacao>
          <Typography variant='span'>Relatório de Retiradas</Typography>
        </Breadcrumbs>
        <PageTitle icon={<ViewDetailsIcon fontSize='large' />} title="Relatório de Retiradas" />
      </Grid>

      <Grid container item xs={12} spacing={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container item xs={12} md={12} spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <SelectEmpresaFilter
              empresas={empresas}
              id_empresas={empresas}
              defaultValue={selectedEmpresaId}
              onChangeValue={(value) => {
                setIsSearched(false);
                setSelectedEmpresaId(value);
                setEmpresaIdSession(value);
                setValue('id_empresas', value, { shouldDirty: true });
                setValue('id_area', '', { shouldDirty: true });
                setValue('id_setor', '', { shouldDirty: true });
                setValue('id_equipamento', '', { shouldDirty: true });
              }}
              textHelper={false}
              isLoading={isLoadingEmpresas}
            />
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
              <Controller
                name='id_setor'
                control={control}
                rules={{ required: false }}
                defaultValue={''}
                render={({ field: { ref, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Setor"
                    disabled={!selectedEmpresaId}
                    onChange={(e) => {
                      setIsSearched(false);
                      setValue('id_setor', e.target.value, { shouldDirty: true })
                      if (e.target.value) {
                        clearErrors("id_setor");
                      }
                    }}
                    error={!!errors.id_setor}
                    helperText={getFormErrorMessage(errors, 'id_setor')}
                    size='small'
                    InputProps={{ style: { backgroundColor: '#fff' } }}
                  >
                    <MenuItem value=''>Todos</MenuItem>
                    {setores.length > 0 ? (
                      [
                        ...setores.filter(option => option.status[0] !== 0).map(option => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.codExterno
                                ? option.codExterno + ' - ' + option.titulo
                                : option.titulo}
                          </MenuItem>
                        )),
                        ...setores.filter(option => option.status[0] === 0).map(option => (
                          <MenuItem key={option.id} value={option.id} disabled>
                            {option.codExterno
                                ? option.codExterno + ' - ' + option.titulo
                                : option.titulo} - (Inativo)
                          </MenuItem>
                        ))
                      ]
                    ) : (
                      <MenuItem disabled>Nenhum disponível</MenuItem>
                    )}
                  </TextField>
                )}
              />
            </TooltipSelectDisabled>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
              <Controller
                name='id_area'
                control={control}
                rules={{ required: false }}
                defaultValue={''}
                render={({ field: { ref, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Área"
                    disabled={!selectedEmpresaId}
                    onChange={(e) => {
                      setIsSearched(false);
                      setValue('id_area', e.target.value, { shouldDirty: true })
                      if (e.target.value) {
                        clearErrors("id_area");
                      }
                    }}
                    error={!!errors.id_area}
                    helperText={getFormErrorMessage(errors, 'id_area')}
                    size='small'
                    InputProps={{ style: { backgroundColor: '#fff' } }}
                  >
                    <MenuItem value=''>Todos</MenuItem>
                    {areas.length > 0 ? (
                      [
                        ...areas.filter(option => option.status[0] !== 0).map(option => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.codExterno
                                ? option.codExterno + ' - ' + option.nome
                                : option.nome}
                          </MenuItem>
                        )),
                        ...areas.filter(option => option.status[0] === 0).map(option => (
                          <MenuItem key={option.id} value={option.id} disabled>
                            {option.codExterno
                                ? option.codExterno + ' - ' + option.nome
                                : option.nome} - (Inativo)
                          </MenuItem>
                        ))
                      ]
                    ) : (
                      <MenuItem disabled>Nenhum disponível</MenuItem>
                    )}
                  </TextField>
                )}
              />
            </TooltipSelectDisabled>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
              <Controller
                name='id_equipamento'
                control={control}
                rules={{ required: false }}
                defaultValue={''}
                render={({ field: { ref, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Equipamento"
                    disabled={!selectedEmpresaId}
                    onChange={(e) => {
                      setIsSearched(false);
                      setValue('id_equipamento', e.target.value, { shouldDirty: true })
                      if (e.target.value) {
                        clearErrors("id_equipamento");
                      }
                    }}
                    error={!!errors.id_equipamento}
                    helperText={getFormErrorMessage(errors, 'id_equipamento')}
                    size='small'
                    InputProps={{ style: { backgroundColor: '#fff' } }}
                  >
                    <MenuItem value=''>Todos</MenuItem>
                    {equipamentos.length > 0 ? (
                        [
                          ...equipamentos.filter(option => option.status !== 0).map(option => (
                              <MenuItem
                                  key={`${option.tipoDispositivo}:${option.id}`}
                                  value={`${option.tipoDispositivo}:${option.id}`}
                              >
                                {option.nome} - ({option.tipoDispositivo === 'armario'
                                  ? 'Armário'
                                  : option.tipo === 'maquina' ? 'Máquina' : 'Balcão'})
                              </MenuItem>
                          )),
                          ...equipamentos.filter(option => option.status === 0).map(option => (
                              <MenuItem
                                  key={`${option.tipoDispositivo}:${option.id}`}
                                  value={`${option.tipoDispositivo}:${option.id}`}
                                  disabled
                              >
                                {option.nome} - ({option.tipoDispositivo === 'armario'
                                  ? 'Armário'
                                  : option.tipo === 'maquina' ? 'Máquina' : 'Balcão'}) - Inativo
                              </MenuItem>
                          ))
                        ]
                    ) : (
                        <MenuItem disabled>Nenhum disponível</MenuItem>
                    )}
                  </TextField>
                )}
              />
            </TooltipSelectDisabled>
          </Grid>
        </Grid>

        <Grid container item xs={12} md={12} spacing={3}>
          <Grid item xs={12} md={3}>
            <DateRangePicker
              value={dateRange}
              bgColor='#fff'
              onChange={(value) => {
                setDateRange(value);
                const [start, end] = value;
                setValue('data_inicio', start ? dayjs(start).format('YYYY-MM-DD') : '');
                setValue('data_fim', end ? dayjs(end).format('YYYY-MM-DD') : '');
                setIsSearched(false);
              }}
              onReset={() => {
                setDateRange([null, null]);
                setValue('data_inicio', '');
                setValue('data_fim', '');
                setIsSearched(false);
              }}
              hasError={errors.data_inicio || errors.data_fim}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              disabled={
                isLoading ||
                Object.keys(errors).length > 0 ||
                !verifyFieldsAreFilled([getValues('id_empresas'), getValues('data_inicio'), getValues('data_fim')])
              }
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "textSecondary" }} /> : <FilterAlt />}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{
          height: supplyMachine.length > 0 ? "auto" : 400,
          minHeight: 400,
          width: '100%',
          overflowX: "auto",
          '& .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer': {
            backgroundColor: '#d8d8d8',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderRadius: 0
          },
        }}>
          <DataGrid
            getRowId={(row) => row.idRetirada}
            rows={supplyMachine.filter(row => row.quantidade > 0)}
            columns={columns.map(col => {
              const { renderCell, ...rest } = col;
              if (col.field === 'action') return col;
              return rest;
            })}
            loading={isLoading}
            pageSizeOptions={[10, 20, 50, 100]}
            // rowCount={numTotalItems}
            paginationMode="client"
            initialState={{
              pagination: {
                paginationModel: { pageSize: 20, page: 0 },
              },
              columns: {
                columnVisibilityModel: {
                  // nomeSetor: false,
                  id: false
                },
              },
            }}
            sx={{
              backgroundColor: '#fff',
              '.MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold !important',
                overflow: 'visible !important',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto !important',
              },
              // '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              //   height: numTotalItems ? '8px' : 0,
              // },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                backgroundColor: grey[600],
                borderRadius: '10px',
              },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
                backgroundColor: grey[700],
              }
            }}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            slots={{
              toolbar: (props) =>
                <CustomToolbarReports
                  reportName="relatorio_retiradas"
                  pdfExport={exportDataToPDF}
                  props={props}
                />
              ,
              loadingOverlay: LinearProgress,
              noRowsOverlay: () => (
                <Box style={{ display: "flex", width: '100%', textAlign: 'center', height: "100%", alignItems: 'center', justifyContent: 'center' }}>
                  {isSearched ?
                    <Typography variant='h6' color='textSecondary'>Nenhum registro encontrado para o filtro selecionado</Typography>
                    :
                    <Typography variant='h6' color='textSecondary'>Selecione uma empresa e o período que deseja filtrar</Typography>
                  }
                </Box>
              )
            }}
          />
        </Box>
      </Grid>

      <RetiradaDetailsModal open={modalOpen} onClose={() => setModalOpen(false)} retiradaDetails={retiradaDetails} />
    </Grid>
  );
}